import { Toaster } from '@/components/ui/toaster'
import { TooltipProvider } from '@/components/ui/tooltip'
import { QueryClient, type QueryClientConfig, QueryClientProvider } from '@tanstack/react-query'
import { NuqsAdapter } from 'nuqs/adapters/react'
import React from 'react'
import { AuthProvider } from './_auth/auth-provider'
import { DomainInitializer } from './_common/domain/use-domain-from-url'
import { Header } from './_common/header'
import { SideNavigation } from './_common/side-navigation'
import { SplitView } from './_common/split-view/split-view'
import { SplitViewProvider } from './_common/split-view/split-view-context'

// 👇 dynamic imports for heavy components
const I18nProvider = React.lazy(() =>
  import('@/i18n/i18n-provider').then((m) => ({ default: m.I18nProvider })),
)

const ReactQueryDevtools = React.lazy(() =>
  import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools })),
)

// 👇 Initialize services dynamically
const initializeServices = async () => {
  // Google Analytics - only load if enabled
  if (import.meta.env.VITE_GOOGLE_ANALYTICS_ID) {
    const ReactGA = await import('react-ga4')
    ReactGA.default.initialize(import.meta.env.VITE_GOOGLE_ANALYTICS_ID)
  }

  // Sentry - only load if enabled
  if (import.meta.env.VITE_SENTRY_DSN) {
    const { init, addIntegration } = await import('@sentry/react')
    const { replayIntegration, browserTracingIntegration, extraErrorDataIntegration } =
      await import('@sentry/browser')

    init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
      enabled: import.meta.env.VITE_SENTRY_ENABLED === 'true',
      debug: import.meta.env.VITE_SENTRY_DEBUG === 'true',
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    })

    addIntegration(replayIntegration())
    addIntegration(browserTracingIntegration())
    addIntegration(extraErrorDataIntegration())
  }
}

// Initialize services after the app loads
if (typeof window !== 'undefined') {
  initializeServices()
}

const QUERY_CLIENT_DEFAULT_CONFIG: QueryClientConfig = {
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false, refetchOnMount: true },
  },
}

function AppRoot() {
  const [queryClient] = React.useState(() => new QueryClient(QUERY_CLIENT_DEFAULT_CONFIG))
  return (
    <QueryClientProvider client={queryClient}>
      <NuqsAdapter>
        <I18nProvider>
          <TooltipProvider>
            <SplitViewProvider>
              <AuthProvider>
                <DomainInitializer>
                  <main className='relative flex h-dvh list-none flex-col overflow-y-hidden font-circular-xx text-neutral-500 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-black/5'>
                    <Header />
                    <div className='flex grow overflow-y-hidden'>
                      <SideNavigation />
                      <div className='h-full w-full overflow-y-auto'>
                        <SplitView />
                      </div>
                    </div>
                    <Toaster />
                  </main>
                </DomainInitializer>
              </AuthProvider>
            </SplitViewProvider>
          </TooltipProvider>
        </I18nProvider>
      </NuqsAdapter>
      {import.meta.env.VITE_ENABLE_REACT_QUERY_DEV_TOOLS === 'true' && (
        <React.Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </React.Suspense>
      )}
    </QueryClientProvider>
  )
}

export { AppRoot }
